import {
  createContext,
  forwardRef,
  useCallback,
  useContext,
  useMemo,
  useState,
  type MouseEventHandler,
  type ReactElement,
} from 'react';
import { ButtonProps, Menu as MuiMenu, type MenuProps as MuiMenuProps } from '@mui/material';
import { ContainedButton } from '../Button';

interface MenuState {
  anchorElement: HTMLButtonElement | null;
  onClose: () => void;
  onOpen: (anchorElement: HTMLButtonElement) => void;
}

const MenuContext = createContext<MenuState | null>(null);

function useMenuContext(): MenuState {
  const context = useContext(MenuContext);
  if (!context) throw new Error('useMenuContext must be used within a Menu');
  return context;
}

type MenuRenderContext = { isOpen: boolean; onClose: () => void };
type MenuChildren =
  | ReactElement[]
  | ((context: MenuRenderContext) => ReactElement<{ children: ReactElement[] }>);

export interface MenuProps extends Omit<MuiMenuProps, 'children' | 'open' | 'onClose'> {
  keepMounted?: boolean;
  children: MenuChildren;
}

export const Menu = forwardRef<HTMLDivElement, MenuProps>(
  ({ children, keepMounted, ...menuProps }, ref) => {
    const [anchorElement, setAnchorElement] = useState<HTMLButtonElement | null>(null);
    const onClose = useCallback(() => setAnchorElement(null), []);
    const isOpen = Boolean(anchorElement);

    const contextValue: MenuState = useMemo(
      () => ({ anchorElement, onClose, onOpen: setAnchorElement }),
      [anchorElement, onClose]
    );

    const { triggerButton, menuContent } = useMemo(() => {
      const childElements =
        typeof children === 'function'
          ? (children as (ctx: MenuRenderContext) => ReactElement<{ children: ReactElement[] }>)({
              isOpen,
              onClose,
            }).props.children
          : children;
      return {
        triggerButton: childElements[0],
        menuContent: childElements.slice(1),
      };
    }, [children, isOpen, onClose]);

    return (
      <MenuContext.Provider value={contextValue}>
        {triggerButton}
        <MuiMenu
          open={isOpen}
          onClose={onClose}
          ref={ref}
          anchorEl={anchorElement}
          keepMounted={keepMounted}
          {...menuProps}
        >
          {menuContent}
        </MuiMenu>
      </MenuContext.Provider>
    );
  }
);

type MenuButtonProps = Omit<ButtonProps, 'onClick'>;

export const MenuButton = forwardRef<HTMLButtonElement, MenuButtonProps>(
  ({ component: Component = ContainedButton, ...props }, ref) => {
    const { onOpen } = useMenuContext();
    const handleOpen: MouseEventHandler<HTMLButtonElement> = useCallback(
      ({ currentTarget }) => onOpen(currentTarget),
      [onOpen]
    );
    return <Component onClick={handleOpen} {...props} ref={ref} />;
  }
);
